import { useEffect } from 'react'
import { HStack, Stack, Text, Button, useDisclosure, Tooltip } from '@chakra-ui/react'
import cx from 'classnames'
import { useStores } from 'hooks/useStores'
import { observer } from 'mobx-react'
import SvgIcon from 'components/SvgIcon'
import { EEsGlobalSearchCollection } from 'constants/elasticSearch'
import { IAsyncSelectOption } from 'interfaces/common'
import { getValidArray } from 'utils/common'
import FilterDialog from './components/FilterDialog'
import MultipleDropdown from './components/MultipleDropdown'
import { checkValidFilter, getDocumentTypeOptions, getTagOptions } from './utils'
import styles from './styles.module.scss'
import { IDropdown } from 'types/common/select'
import { searchSortByOptions } from 'components/Topbar/constants'
import Dropdown from 'components/Dropdown'

function SearchPageHeader() {
  const { searchStore, organizationStore, tagStore, documentTypeStore } = useStores()
  const { organization } = organizationStore
  const { displayTypes = [EEsGlobalSearchCollection.PROCESS], isSearching, searchFilter } = searchStore
  const organizationId: number = organization?.id ?? 0
  const { isOpen: isOpenFilter, onOpen: onOpenFilter, onClose: onCloseFilter } = useDisclosure()

  function toggleFilterProcess(): void {
    if (displayTypes.includes(EEsGlobalSearchCollection.PROCESS)) {
      searchStore.setDisplayTypes(displayTypes.filter(item => item !== EEsGlobalSearchCollection.PROCESS))
    } else {
      searchStore.setDisplayTypes([...displayTypes, EEsGlobalSearchCollection.PROCESS])
    }
    searchStore.search()
  }

  function toggleFilterCollection(): void {
    if (displayTypes.includes(EEsGlobalSearchCollection.COLLECTION)) {
      searchStore.setDisplayTypes(displayTypes.filter(item => item !== EEsGlobalSearchCollection.COLLECTION))
    } else {
      searchStore.setDisplayTypes([...displayTypes, EEsGlobalSearchCollection.COLLECTION])
    }
    searchStore.search()
  }

  function changeFilterTags(tags: IAsyncSelectOption<number>[]): void {
    searchStore.changeFilterTags(tags)
    searchStore.search()
  }

  function changeFilterDocumentTypes(documentTypes: IAsyncSelectOption<number>[]): void {
    searchStore.changeFilterDocumentTypes(documentTypes)
    searchStore.search()
  }

  function clearFilters(): void {
    searchStore.clearSearchFilter()
    searchStore.search()
  }

  function changeFilterSortOrder(sort: string): void {
    searchStore.changeSearchFilter({ ...searchStore.searchFilter, sort })
    searchStore.search()
  }

  useEffect(() => {
    tagStore.fetchTags({ where: { organizationId }, order: ['name ASC'] })
    documentTypeStore.fetchDocumentTypes({ where: { organizationId }, order: ['views DESC'], include: ['iconBuilder'] })
  }, [])

  return (
    <Stack flexDirection={{ base: 'column', md: 'row' }} gap={4} spacing={0} width="full" paddingLeft={{ base: '10px', md: 0 }}>
      <Stack flexDirection={{ base: 'column', md: 'row' }} gap={4} spacing={0} flex={1}>
        <HStack spacing={2}>
          <Tooltip
            className={styles.tooltip}
            label="Process"
            hasArrow
            shouldWrapChildren={true}
            background="#5c5c5c"
            placement="top-start"
          >
            <SvgIcon
              className={cx(styles.icon, styles.process, {
                [styles.active]: displayTypes.includes(EEsGlobalSearchCollection.PROCESS)
              })}
              onClick={toggleFilterProcess}
              iconName="process"
              size={32}
            />
          </Tooltip>
          <Tooltip
            className={styles.tooltip}
            label="Collection"
            hasArrow
            shouldWrapChildren={true}
            background="#5c5c5c"
            placement="top-start"
          >
            <SvgIcon
              className={cx(styles.icon, styles.collection, {
                [styles.active]: displayTypes.includes(EEsGlobalSearchCollection.COLLECTION)
              })}
              onClick={toggleFilterCollection}
              iconName="collection"
              size={32}
            />
          </Tooltip>
          <Button
            height={8}
            backgroundColor="white"
            border="1px solid #E2E8F0"
            padding={{ base: '8px', md: '10px' }}
            display={{ base: 'inline-flex', md: 'none' }}
            onClick={onOpenFilter}
          >
            <HStack spacing={2} alignItems="center">
              <SvgIcon className={styles.icon} iconName="sort" size={14} />
              {searchStore.countFilter() > 0 && (
                <Text color="gray.700" fontSize="md" fontWeight={500}>{`(${searchStore.countFilter()})`}</Text>
              )}
            </HStack>
          </Button>
        </HStack>
        <Stack display={{ base: 'none', md: 'block' }} width={{ base: 'full', md: '200px' }}>
          <MultipleDropdown
            width={{ base: 'full', md: '200px' }}
            name="documentTypes"
            items={getValidArray(searchFilter.documentTypes)}
            options={getDocumentTypeOptions(documentTypeStore.documentTypes)}
            setItems={changeFilterDocumentTypes}
            placeHolder="Document Type"
            isDisabled={isSearching ?? false}
          />
        </Stack>
        <Stack display={{ base: 'none', md: 'block' }} width={{ base: 'full', md: '200px' }}>
          <MultipleDropdown
            width={{ base: 'full', md: '200px' }}
            name="tags"
            items={getValidArray(searchFilter.tags)}
            options={getTagOptions(tagStore.tags)}
            setItems={changeFilterTags}
            placeHolder="Tag"
            isDisabled={isSearching ?? false}
          />
        </Stack>
         <HStack spacing={4} justify="space-between" flex={1} display={{ base: 'none', md: 'flex' }}>
           <HStack spacing={4}>
             <Button
               height={8}
               backgroundColor="white"
               gap={{ base: 0, lg: 2 }}
               fontWeight={500}
               border={`1px solid ${searchStore.countFilter() > 0 ? '#00A9EB' : '#E2E8F0'}`}
               borderRadius="8px"
               cursor="pointer"
               padding={{ base: '8px', lg: '10px' }}
               variant="solid"
               onClick={onOpenFilter}
               isDisabled={isSearching ?? false}
             >
               <SvgIcon className={styles.icon} iconName="sort" size={14} />
               <Text
                 marginBottom="0"
                 fontWeight={500}
                 fontSize={{ base: '0px', lg: '14px' }}
                 lineHeight="20px"
                 color="gray.700"
               >
                 {`More Filters ${searchStore.countFilter() > 0 ? `(${searchStore.countFilter()})` : ''}`}
               </Text>
             </Button>
             <Text
               color="gray.700"
               fontSize="14px"
               fontWeight={500}
               lineHeight={2}
               cursor="pointer"
               onClick={clearFilters}
               hidden={!checkValidFilter(searchFilter)}
               whiteSpace="nowrap"
             >
               Clear filters
             </Text>
           </HStack>
            <Dropdown
                name="sort"
                titleDropdown="SORT BY"
                fontSize="sm"
                width={{ base: 'full', md: '160px' }}
                height="32px"
                options={searchSortByOptions}
                item={{
                  title: searchFilter.sort ? `${searchFilter.sort}` : 'Most relevant',
                  value: searchFilter.sort ? `${searchFilter.sort}` : 'Most relevant'
                }}
                setValue={(name: string, dropdownValue: IDropdown) => changeFilterSortOrder(`${dropdownValue.value}`)}
                isDisabled={isSearching ?? false}
              />
         </HStack>
       </Stack>
      <FilterDialog isOpen={isOpenFilter} onClose={onCloseFilter} />
    </Stack>
  )
}

export default observer(SearchPageHeader)
